import { Point, Wall, WindowElement } from '../types';
import { WALL_THICKNESS } from '../constants/floorPlanConstants';

const DEFAULT_BAY_WINDOW_WIDTH = 120; // Width of the bay window
const DEFAULT_BAY_WINDOW_DEPTH = 60; // Depth of the bay window projection
const DEFAULT_BAY_FRONT_WIDTH = 80; // Width of the front section

function getClosestPointOnSegment(point: Point, start: Point, end: Point): Point {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (dx === 0 && dy === 0) return start;
    const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy);
    const clampedT = Math.max(0, Math.min(1, t));
    return { x: start.x + clampedT * dx, y: start.y + clampedT * dy };
}

// Create bay window (Victorian style) with angled sides
// Side placement works like radiators - drag to different sides of wall to change placement
export function createCantedWindowElement(
    point: Point,
    wall: Wall & { tempId: string, stableId: string },
    currentFloor: number
): WindowElement | null {
    const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
    if (wallLength < DEFAULT_BAY_WINDOW_WIDTH) return null;

    const closestPoint = getClosestPointOnSegment(point, wall.start, wall.end);
    const distFromStart = Math.hypot(closestPoint.x - wall.start.x, closestPoint.y - wall.start.y);

    let positionOnWall = distFromStart / wallLength;

    const halfWindowWidthOnWall = (DEFAULT_BAY_WINDOW_WIDTH / 2) / wallLength;
    positionOnWall = Math.max(halfWindowWidthOnWall, Math.min(positionOnWall, 1 - halfWindowWidthOnWall));

    const windowCenter = {
        x: wall.start.x + (wall.end.x - wall.start.x) * positionOnWall,
        y: wall.start.y + (wall.end.y - wall.start.y) * positionOnWall,
    };

    // Calculate which side of the wall the user clicked on (like radiators)
    const wallDx = wall.end.x - wall.start.x;
    const wallDy = wall.end.y - wall.start.y;
    
    // Use cross product to determine side: positive = one side, negative = other side
    const crossProduct = wallDx * (point.y - wall.start.y) - wallDy * (point.x - wall.start.x);
    const sideValue = crossProduct > 0 ? 1 : -1;

    const newWindow: WindowElement = {
        id: `bay-window-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'window',
        wallId: wall.stableId,
        positionOnWall,
        width: DEFAULT_BAY_WINDOW_WIDTH,
        height: 60,
        depth: DEFAULT_BAY_WINDOW_DEPTH,
        trickleVents: 0,
        isDraughtProofed: false,
        windowType: 'pvc',
        condition: 'good',
        floor: currentFloor,
        position: windowCenter,
        windowInstallationType: 'canted',
        side: sideValue // Store numeric side value like radiators
    } as WindowElement & { side: number };

    return newWindow;
}

// Draw Victorian-style bay window with angled sides
export function drawCantedWindow(
    ctx: CanvasRenderingContext2D, 
    windowEl: WindowElement, 
    wall: Wall, 
    scale: number, 
    isSelected?: boolean, 
    allWalls?: Wall[]
) {
    const { position, width = DEFAULT_BAY_WINDOW_WIDTH, depth = DEFAULT_BAY_WINDOW_DEPTH } = windowEl;
    const sideValue = (windowEl as any).side || 1; // Default to side 1 if not set
    
    ctx.save();
    
    // Calculate wall direction and perpendicular
    const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
    const wallLength = Math.hypot(wallVector.x, wallVector.y);
    if (wallLength === 0) {
        ctx.restore();
        return;
    }
    
    const wallDirection = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
    
    // Calculate perpendicular based on side value (like radiators)
    const wallAngle = Math.atan2(wallVector.y, wallVector.x);
    const basePerpendicular = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
    
    // Apply side multiplier to perpendicular
    const perpendicular = { 
        x: basePerpendicular.x * sideValue, 
        y: basePerpendicular.y * sideValue 
    };
    
    const halfWidth = width / 2;
    const wallHalfThickness = WALL_THICKNESS / 2;
    const frontWidth = DEFAULT_BAY_FRONT_WIDTH / 2;
    
    // Victorian Bay Window geometry - 4 points forming angled bay
    // Points on the wall (base of bay window)
    const wallLeft = {
        x: position.x - wallDirection.x * halfWidth,
        y: position.y - wallDirection.y * halfWidth
    };
    const wallRight = {
        x: position.x + wallDirection.x * halfWidth,
        y: position.y + wallDirection.y * halfWidth
    };
    
    // Front corners (projecting outward)
    const frontLeft = {
        x: position.x - wallDirection.x * frontWidth + perpendicular.x * depth,
        y: position.y - wallDirection.y * frontWidth + perpendicular.y * depth
    };
    const frontRight = {
        x: position.x + wallDirection.x * frontWidth + perpendicular.x * depth,
        y: position.y + wallDirection.y * frontWidth + perpendicular.y * depth
    };

    // Selection highlighting
    if (isSelected) {
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 4 / scale;
        ctx.setLineDash([8 / scale, 4 / scale]);
        ctx.beginPath();
        ctx.moveTo(wallLeft.x, wallLeft.y);
        ctx.lineTo(frontLeft.x, frontLeft.y);
        ctx.lineTo(frontRight.x, frontRight.y);
        ctx.lineTo(wallRight.x, wallRight.y);
        ctx.closePath();
        ctx.stroke();
        ctx.setLineDash([]);
    }
    
    // Draw Victorian bay window outline - thick black lines
    ctx.fillStyle = '#ffffff'; // White fill for window
    ctx.strokeStyle = '#000000'; // Black outline
    ctx.lineWidth = 3 / scale; // Thick lines like reference image
    
    // Draw the bay window shape (4-sided polygon)
    ctx.beginPath();
    ctx.moveTo(wallLeft.x, wallLeft.y);
    ctx.lineTo(frontLeft.x, frontLeft.y);
    ctx.lineTo(frontRight.x, frontRight.y);
    ctx.lineTo(wallRight.x, wallRight.y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    // Draw angled side lines (emphasized)
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 3 / scale;
    
    // Left angled side
    ctx.beginPath();
    ctx.moveTo(wallLeft.x, wallLeft.y);
    ctx.lineTo(frontLeft.x, frontLeft.y);
    ctx.stroke();
    
    // Right angled side
    ctx.beginPath();
    ctx.moveTo(wallRight.x, wallRight.y);
    ctx.lineTo(frontRight.x, frontRight.y);
    ctx.stroke();
    
    // Front section
    ctx.beginPath();
    ctx.moveTo(frontLeft.x, frontLeft.y);
    ctx.lineTo(frontRight.x, frontRight.y);
    ctx.stroke();
    
    ctx.restore();
}